export type HopDestinationType = "CUSTOMER" | "SUPPLIER";

export type HopDestinationPayload = {
  type: HopDestinationType;
  customerId?: string;
  supplierId?: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude?: number;
      longitude?: number;
    };
    contact?: {
      name?: string;
      phoneNumber?: string;
    };
  };
};

/** Group key so orders with the same destination share one shipment. */
export function destinationGroupKey(destination: HopDestinationPayload): string {
  const id =
    destination.type === "CUSTOMER"
      ? destination.customerId || ""
      : destination.supplierId || "";
  const line = destination.address?.addressLine1 || "";
  return `${destination.type}:${id}:${line}`;
}

/**
 * Build hop destination payload from order.logisticsPath isDestination entry
 * (set at R2D Self Dispatch). Used to batch Create Shipment by path dest.
 */
export function hopDestinationFromLogisticsPath(
  logisticsPath?: Array<{
    type?: string;
    customerId?: string | { toString(): string };
    supplierId?: string | { toString(): string };
    isDestination?: boolean;
    address?: HopDestinationPayload["address"];
  }> | null,
): HopDestinationPayload | null {
  if (!Array.isArray(logisticsPath)) return null;
  const dest = logisticsPath.find((e) => e?.isDestination === true);
  if (!dest?.type || !dest.address?.addressLine1) return null;

  const type = dest.type === "SUPPLIER" ? "SUPPLIER" : "CUSTOMER";
  const customerId =
    dest.customerId != null ? String(dest.customerId) : undefined;
  const supplierId =
    dest.supplierId != null ? String(dest.supplierId) : undefined;

  if (type === "CUSTOMER" && !customerId) return null;
  if (type === "SUPPLIER" && !supplierId) return null;

  return {
    type,
    ...(customerId ? { customerId } : {}),
    ...(supplierId ? { supplierId } : {}),
    address: { ...dest.address },
  };
}

export function buildCustomerHopDestination(orderData: {
  customerId?: string;
  customer?: { _id?: string; name?: string; mobileNumber?: string };
  shipmentAddress?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    location?: { latitude?: number; longitude?: number };
  };
}): HopDestinationPayload | null {
  const customerId =
    orderData.customerId?.toString?.() ||
    orderData.customer?._id?.toString?.() ||
    "";
  const addr = orderData.shipmentAddress;
  // Require id + at least a street line; city/state may be empty on some orders
  if (!customerId || !addr?.addressLine1?.trim()) {
    return null;
  }
  return {
    type: "CUSTOMER",
    customerId,
    address: {
      addressLine1: addr.addressLine1.trim(),
      ...(addr.addressLine2 ? { addressLine2: addr.addressLine2 } : {}),
      city: addr.city?.trim() || "",
      state: addr.state?.trim() || "",
      ...(addr.pinCode ? { pinCode: addr.pinCode } : {}),
      ...(addr.country ? { country: addr.country } : {}),
      ...(addr.location ? { location: { ...addr.location } } : {}),
      contact: {
        name: orderData.customer?.name || "Customer",
        phoneNumber: orderData.customer?.mobileNumber || "",
      },
    },
  };
}

export function buildSupplierHopDestination(supplier: {
  _id?: string;
  businessName?: string;
  name?: string;
  mobileNumber?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    location?: { latitude?: number; longitude?: number };
  };
  branches?: Array<{
    _id?: string;
    branchName?: string;
    branchPrimaryMobileNumber?: string;
    branchAddress?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      pinCode?: string;
      country?: string;
      location?: { latitude?: number; longitude?: number };
    };
  }>;
  selectedBranchId?: string;
}): HopDestinationPayload | null {
  const supplierId = supplier._id?.toString?.() || "";
  if (!supplierId) return null;

  let addr = supplier.address;
  let contactName = supplier.businessName || supplier.name || "Supplier";
  let phone = supplier.mobileNumber || "";

  if (supplier.selectedBranchId && Array.isArray(supplier.branches)) {
    const branch = supplier.branches.find(
      (b) => b._id?.toString?.() === supplier.selectedBranchId,
    );
    if (branch?.branchAddress) {
      addr = branch.branchAddress;
      contactName = branch.branchName || contactName;
      phone = branch.branchPrimaryMobileNumber || phone;
    }
  }

  if (!addr?.addressLine1 || !addr?.city || !addr?.state) {
    return null;
  }

  return {
    type: "SUPPLIER",
    supplierId,
    address: {
      addressLine1: addr.addressLine1,
      ...(addr.addressLine2 ? { addressLine2: addr.addressLine2 } : {}),
      city: addr.city,
      state: addr.state,
      ...(addr.pinCode ? { pinCode: addr.pinCode } : {}),
      ...(addr.country ? { country: addr.country } : {}),
      ...(addr.location ? { location: { ...addr.location } } : {}),
      contact: {
        name: contactName,
        phoneNumber: phone,
      },
    },
  };
}
